/*!
 *  Content type select form field.
 *  Author: Bjorn Tollstrom <bjorn@rodolfo.se>
 */

import React from "react";
import PropTypes from "prop-types";
import {ObjectCompare} from "Functions";
import FilterField from "Components/UI/Field/FilterField";

class ContentTypeField extends React.Component
{
    constructor(props)
    {
        super(props);

        this.Types = {
            v1: {
                "content::article": "Article",
                //"capturedvideo": "Captured Video",
                "community": "Community",
                "externalLink": "External Link",
                "event": "Event",
                //"learningplan": "Learning Plan",
                "question": "Question",
                "scormcourse": "Scorm Course",
                //"topic": "Topic",
                "uploadedfile": "Uploaded File",
                "user": "User",
                "video": "Video"
            },
            v2: {
                "Topic": "Topics",
                "UploadedFile": "Files",
                "Content::Article": "Articles",
                "ExternalLink": "Links",
                "ScormCourse": "Courses",
                "LearningPlan": "Learning Plans",
                "Question": "Questions",
                "Person": "People",
                "Event": "Events",
                "Video": "Videos",
                "Community": "Communities"
            }
        };
        this.state =
        {
            filters: {},
            types: null
        };
    }

    componentDidMount()
    {
        const {multiple, types, value, version} = this.props;
        
        if (multiple)
        {
            this.setState({
                filters: typeof value === "string" ? {contentType: [value]} : value,
                types: this.FilterTypesProp(types, version)
            });
        }
        else
        {
            this.setState({
                filters: {contentType: [value] || -1},
                types: this.FilterTypesProp(types, version)
            });
        }
    }

    componentDidUpdate(prevProps)
    {
        const {multiple: m1, types: t1, value: va1, version: ve1} = this.props;
        const {multiple: m2, types: t2, value: va2, version: ve2} = prevProps;
        const updatedState = {};
        let needsUpdate = false;

        if (!ObjectCompare(va1, va2) || m1 !== m2)
        {
            if (m1)
            {
                updatedState.filters = typeof va1 === "string" ? {contentType: [va1]} : va1;
            }
            else
            {
                updatedState.filters = {contentType: [va1] || -1};
            }
            needsUpdate = true;
        }

        if (!ObjectCompare(t1, t2) || ve1 !== ve2)
        {
            updatedState.types = this.FilterTypesProp(t1, ve1);
            needsUpdate = true;
        }

        if (needsUpdate)
        {
            this.setState(updatedState);
        }
    }

    /**
     * Assign labels to the types prop, if it's been supplied.
     * @param array|null types - An array of type keys.
     * @param string version - The version to use for content keys.
     * @return object|null - Parsed types.
     */

    FilterTypesProp = (types, version) =>
    {
        if (!Array.isArray(types))
        {
            return null;
        }
        const Types = {};
        types.forEach(type =>
        {
            Types[type] = this.Types[version][type] || type
        });
        return Types;
    }

    /**
     * Adjust field items or load new events when the filters change.
     * @param object e - The event object.
     * @param object filters - New filters.
     * @return void
     */

    SetFilter = (e, filters) =>
    {
        const {id, inForm, onChange} = this.props;
        const {filters: currentFilters} = this.state;
        this.setState({filters}, () =>
        {
            if (inForm)
            {
                onChange(e, filters, id);
            }
            else
            {
                for (let key in filters)
                {
                    if (filters[key] === currentFilters[key])
                    {
                        continue;
                    }
                    onChange(e, filters[key], key, id);
                }
            }
        });
    }

    /**
     * Set the content type filter label.
     * @return string - The label
     */

    SetFilterLabel = () =>
    {
        const {placeholder, selectedLabel, version} = this.props;
        const {filters, types: typesFromProps} = this.state;
        const typeOptions = typesFromProps || this.Types[version];
        if (!filters)
        {
            return placeholder;
        }
        if (typeof selectedLabel === "function")
        {
            if (filters.contentType === "string")
            {
                return selectedLabel([typeOptions[filters.contentType]], filters);
            }
            if (Array.isArray(filters.contentType))
            {
                return selectedLabel(filters.contentType.map(key => typeOptions[key]).filter(label => !!label), filters)
            }
        }
        if (typeof filters.contentType === "string")
        {
            return typeOptions[filters.contentType] || placeholder;
        }
        if (Array.isArray(filters.contentType))
        {
            const types = filters.contentType.map(key => typeOptions[key]).filter(label => !!label);
            return types.join(", ");
        }
        return placeholder;
    }

    render()
    {
        const {additionalFields, className, multiple, placeholder, version, ...inheritProps} = this.props;
        const {filters, types} = this.state;
        const CA = ["ContentTypeField"];
        if (className)
        {
            CA.push(className);
        }
        return <FilterField
            {...inheritProps}
            className={CA.join(" ")}
            closeOnChange={false}
            fields={{
                contentType:
                {
                    options: types || this.Types[version],
                    multiple,
                    placeholder,
                    type: "choice"
                },
                ...additionalFields
            }}
            onChange={this.SetFilter}
            selectedLabel={this.SetFilterLabel}
            values={filters}
        />
    }
}

ContentTypeField.propTypes =
{
    addEventListener: PropTypes.object,
    className: PropTypes.string,
    inForm: PropTypes.bool,
    multiple: PropTypes.bool,
    onChange: PropTypes.func,
    selectedLabel: PropTypes.func,
    types: PropTypes.array,
    version: PropTypes.string
};

ContentTypeField.defaultProps =
{
    additionalFields: {},
    className: "",
    inForm: false,
    multiple: false,
    onChange: () => {},
    selectedLabel: null,
    types: null,
    version: "v1"
};

export default ContentTypeField;